import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, StatusBadge, Meter } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { useCan } from "../lib/auth";
import { DELIVERABLE_PHASES } from "../lib/program";
import { StudioLinks } from "../components/StudioLinks";
import { stageStates, releasableStage } from "../lib/drawingStages";
import { fmtDate } from "../lib/format";
import type { ProgramTask, Deliverable } from "../lib/types";

const PHASE_COLOR: Record<string, string> = {
  Engagement: "var(--ink-3)", Concept: "var(--sky)", Developed: "var(--sage)", Presentation: "var(--accent)", Documentation: "var(--gold)",
};
const DISC_COLOR: Record<string, string> = {
  Architecture: "var(--accent)", Structural: "var(--sage)", MEP: "var(--sky)", Interiors: "var(--gold)",
};
const NEXT_STATUS: Record<string, "todo" | "doing" | "issued"> = { todo: "doing", doing: "issued", issued: "todo" };

export default function Program() {
  const p = useCurrent();
  const canEdit = useCan("stages");
  if (!p) return null;
  const all = p.program;
  const dels = p.deliverables ?? [];
  const issued = dels.filter((d) => d.status === "issued").length;
  const min = Math.min(...all.map((t) => +new Date(t.start)));
  const max = Math.max(...all.map((t) => +new Date(t.end)));
  const span = max - min || 1;
  const pos = (iso: string) => ((+new Date(iso) - min) / span) * 100;

  // month ticks
  const ticks: { left: number; label: string }[] = [];
  const d = new Date(min); d.setDate(1);
  while (+d <= max) { ticks.push({ left: ((+d - min) / span) * 100, label: d.toLocaleDateString("en-US", { month: "short" }) }); d.setMonth(d.getMonth() + 1); }
  const todayLeft = ((+new Date() - min) / span) * 100;

  const doneCount = all.filter((t) => t.status === "done").length;

  return (
    <Page>
      <PageHead kicker={`${p.name} · Delivery`} title="Design work program" />
      <MindmapBar p={p} canEdit={canEdit} />
      <div className="row wrap" style={{ gap: 22, marginBottom: 22 }}>
        <div className="badge">{all.length} tasks</div>
        <div className="badge ok"><span className="dot" />{doneCount} complete</div>
        <div className="badge accent"><span className="dot" />{all.filter((t) => t.status === "doing").length} in progress</div>
        <div className="badge danger"><span className="dot" />{all.filter((t) => t.status === "blocked").length} blocked</div>
      </div>

      {/* IFT drawing package — details */}
      <div className="row between reveal" style={{ marginBottom: 12 }}>
        <div><div className="kicker">Deliverables</div><h3 className="serif" style={{ fontSize: 22, marginTop: 4 }}>IFT drawing package</h3></div>
        {canEdit && (dels.length === 0
          ? <button className="btn accent sm" onClick={() => actions.generateIftPackage(p.id)}><Icon.layers width={15} />Generate package</button>
          : <button className="btn ghost sm" onClick={() => actions.generateIftPackage(p.id)}><Icon.layers width={14} />Regenerate</button>)}
      </div>
      {dels.length > 0 && (
        <div className="card pad reveal" style={{ marginBottom: 12 }}>
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="kicker">IFT package readiness</span>
            <span className="mono" style={{ fontSize: 13 }}>{issued} / {dels.length} sheets issued</span>
          </div>
          <Meter value={dels.length ? (issued / dels.length) * 100 : 0} />
          <div className="row between wrap" style={{ gap: 10, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
            <span className="dim" style={{ fontSize: 12.5 }}>Send this register to the Project Studio — it becomes the drawing set on site, or the tender documents.</span>
            <StudioLinks p={p} showCount />
          </div>
        </div>
      )}
      <div className="card reveal" style={{ overflow: "hidden", marginBottom: 26 }}>
        {DELIVERABLE_PHASES.map((phase) => {
          const rows = dels.filter((d) => d.phase === phase);
          if (!rows.length) return null;
          const ph = rows.filter((d) => d.status === "issued").length;
          return (
            <div key={phase}>
              <div className="row between" style={{ padding: "12px 20px", background: "var(--paper-2)", borderTop: "1px solid var(--line)" }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{phase}{phase === "IFT" && <span className="dim" style={{ fontWeight: 400 }}> · issued for tender</span>}</span>
                <span className="mono dim" style={{ fontSize: 12 }}>{ph}/{rows.length}</span>
              </div>
              {rows.map((d) => (
                <div key={d.id} className="row between" style={{ padding: "10px 20px", borderTop: "1px solid var(--line)", gap: 12 }}>
                  <div className="row" style={{ gap: 12, minWidth: 0 }}>
                    <span className="mono" style={{ fontSize: 12.5, width: 62, color: "var(--ink-2)", flexShrink: 0 }}>{d.code}</span>
                    <span style={{ fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.title}</span>
                    <span className="badge" style={{ fontSize: 10, color: DISC_COLOR[d.discipline], borderColor: DISC_COLOR[d.discipline] }}>{d.discipline}</span>
                  </div>
                  <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                    {(d.scale || d.sheet) && <span className="mono dim" style={{ fontSize: 10.5 }}>{[d.scale, d.sheet].filter(Boolean).join(" · ")}</span>}
                    {d.revision && <span className="mono dim" style={{ fontSize: 10.5 }}>{d.revision}</span>}
                    {d.dueOn && <span className="badge" style={{ fontSize: 9.5 }} title="Scheduled on the mind map"><Icon.calendar width={10} />{fmtDate(d.dueOn)}</span>}
                    <StatusBadge status={d.status} />
                    {canEdit && <button className="chip" style={{ padding: "4px 10px", fontSize: 11.5 }} onClick={() => actions.setDeliverableStatus(p.id, d.id, NEXT_STATUS[d.status])}>Advance</button>}
                    {canEdit && <button className="iconbtn" style={{ width: 28, height: 28 }} onClick={() => actions.removeDeliverable(p.id, d.id)}><Icon.close width={13} /></button>}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        {dels.length === 0 && <div className="dim" style={{ padding: 26, textAlign: "center" }}>No drawing package yet. {canEdit && "Generate the IFT package to lay out every sheet."}</div>}
      </div>

      <div className="row between reveal" style={{ marginBottom: 12 }}>
        <div><div className="kicker">Timeline</div><h3 className="serif" style={{ fontSize: 22, marginTop: 4 }}>Programme & activities</h3></div>
      </div>
      <div className="card pad reveal" style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 720 }}>
          {/* header row with month ticks */}
          <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, marginBottom: 10 }}>
            <div className="kicker">Task</div>
            <div style={{ position: "relative", height: 16 }}>
              {ticks.map((t, i) => <span key={i} className="mono" style={{ position: "absolute", left: `${t.left}%`, fontSize: 10, color: "var(--ink-3)" }}>{t.label}</span>)}
            </div>
          </div>

          <div style={{ position: "relative" }}>
            {all.map((t) => (
              <div key={t.id} style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--line)" }}>
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: PHASE_COLOR[t.phase] ?? "var(--ink-3)", flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</span>
                  </div>
                  <div className="dim" style={{ fontSize: 11, marginLeft: 16 }}>{t.owner} · {t.phase}</div>
                </div>
                <div style={{ position: "relative", height: 30 }}>
                  <Bar t={t} left={pos(t.start)} width={Math.max(2, pos(t.end) - pos(t.start))} color={PHASE_COLOR[t.phase] ?? "var(--ink-3)"} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}

function Bar({ t, left, width, color }: { t: ProgramTask; left: number; width: number; color: string }) {
  const blocked = t.status === "blocked";
  return (
    <div style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: 4, height: 22 }}>
      <div title={`${fmtDate(t.start)} → ${fmtDate(t.end)}`} style={{
        height: "100%", borderRadius: 7, position: "relative", overflow: "hidden",
        background: blocked ? "repeating-linear-gradient(45deg, var(--danger), var(--danger) 5px, color-mix(in srgb,var(--danger) 60%, #000) 5px, color-mix(in srgb,var(--danger) 60%, #000) 10px)" : `color-mix(in srgb, ${color} 22%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 50%, transparent)`,
      }}>
        {!blocked && <div style={{ position: "absolute", inset: 0, width: `${t.progress}%`, background: color, opacity: .85 }} />}
        <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 10, fontWeight: 600, color: t.progress > 40 && !blocked ? "#fff" : "var(--ink)", mixBlendMode: "normal" }}>{t.status === "done" ? "✓" : blocked ? "blocked" : `${t.progress}%`}</span>
      </div>
    </div>
  );
}

/* The work programme, delegated. One board on the Task Mind Map, one node per
   task; completion flows back here and tells the client. */
/* Drawings go to the mind map one design stage at a time — Schematic Design
   first, the next only once the client accepts the one before. Sending a stage
   schedules its sheets and the dates land on the calendar. */
function MindmapBar({ p, canEdit }: { p: any; canEdit: boolean }) {
  const [busy, setBusy] = useState<"send" | "probe" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const mm = p.mindmap;
  const states = stageStates(p);
  const releasable = releasableStage(p);

  async function send() {
    setBusy("send"); setMsg(null);
    const r = await actions.sendDrawingStageToMindmap(p.id);
    setMsg(r.ok ? `${r.stage} — ${r.count} drawings on the board, scheduled onto your calendar.` : r.reason ?? "Couldn't send it.");
    setBusy(null);
  }
  async function probe() {
    setBusy("probe"); setMsg(null);
    const r = await actions.probeMindmap(p.id);
    setMsg(!r.ok ? (r.reason ?? "Couldn't reach the board.")
      : r.completed ? `${r.completed} item${r.completed > 1 ? "s" : ""} completed — the register and your client are updated.`
      : "Up to date — nothing newly completed.");
    setBusy(null);
  }

  const countFor = (phase: string) => (p.deliverables ?? []).filter((d: any) => d.phase === phase).length;

  return (
    <div className="card pad reveal" style={{ marginBottom: 18 }}>
      <div className="row between wrap" style={{ gap: 12, marginBottom: 12 }}>
        <div>
          <div className="row" style={{ gap: 8 }}>
            <span className="kicker">Drawings → Task mind map</span>
            {mm?.sheetId && <span className="badge ok" style={{ fontSize: 9 }}><span className="dot" />board linked</span>}
          </div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 4, maxWidth: 620 }}>
            Release the drawings a stage at a time. Each sheet becomes a scheduled task on the board; finishing one there issues it here and tells the client. The next stage unlocks once the client accepts the one before.
          </div>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {mm?.url && <a className="btn ghost sm" href={mm.url} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={14} />Open board</a>}
          {canEdit && mm?.stages && Object.keys(mm.stages).length > 0 && <button className="btn soft sm" onClick={probe} disabled={!!busy}><Icon.swap width={14} />{busy === "probe" ? "Checking…" : "Check progress"}</button>}
          {canEdit && releasable.stage && <button className="btn accent sm" onClick={send} disabled={!!busy}><Icon.send width={14} />{busy === "send" ? "Sending…" : `Send ${releasable.stage.label}`}</button>}
        </div>
      </div>

      {/* the three stages, in release order */}
      <div className="row wrap" style={{ gap: 8 }}>
        {states.map(({ stage, state }) => {
          const tone = state.status === "sent" ? "var(--ok)" : state.status === "ready" ? "var(--accent)" : "var(--line-2)";
          return (
            <div key={stage.key} className="panel" style={{ flex: "1 1 180px", padding: "10px 12px", borderLeft: `3px solid ${tone}` }}>
              <div className="row between" style={{ gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{stage.label}</span>
                <span className="dim mono" style={{ fontSize: 10.5 }}>{countFor(stage.phase)} sheets</span>
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>
                {state.status === "sent" ? <span style={{ color: "var(--ok)" }}>On the board · {fmtDate(state.at.slice(0, 10))}</span>
                  : state.status === "ready" ? <span style={{ color: "var(--accent-ink)" }}>Ready to send</span>
                  : <>Locked — {state.reason}</>}
              </div>
            </div>
          );
        })}
      </div>
      {msg && (
        <div className="dim" style={{ fontSize: 12.5, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
          {msg}
          {/not configured|Integrations/i.test(msg) && <> <a href="/integrations" style={{ color: "var(--accent-ink)", fontWeight: 600 }}>Connect the Mind Map →</a></>}
        </div>
      )}
    </div>
  );
}
